import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Container, Row, Col, Button, ButtonGroup } from 'reactstrap';
import 'bootstrap/dist/css/bootstrap.css';
import "react-table/react-table.css";
import CompanyContext from '../../auth/CompanyContext';
import * as utilities from '../../utilities';
import ContactsTableView from './Documents/ContactsTableView';
import ContactsTreeView from './Documents/ContactsTreeView';
import Loading from '../Loading/Loading';

const normalizeValue = (value) => String(value || '').trim().toLowerCase();
const normalizeWhitespace = (value) => String(value || '').replace(/\s+/g, ' ').trim();
const toHouseholdFolder = (value) => {
    const normalized = normalizeWhitespace(value)
        .toLowerCase()
        .replace(/^households\//i, '')
        .replace(/^\/+/, '')
        .replace(/\/+$/, '');
    return normalized ? `households/${normalized}/` : '';
};

const getHouseholdKeyFromPath = (path) => {
    const parts = String(path || '')
        .split('/')
        .map(part => normalizeValue(part))
        .filter(Boolean);
    const householdsIdx = parts.indexOf('households');
    return householdsIdx >= 0 ? parts[householdsIdx + 1] || '' : '';
};

const getFileExtension = (name = '', path = '') => {
    const source = String(name || path || '').toLowerCase();
    const ext = source.split('.').pop();
    return ext || '';
};

const isFolderEntry = (entry) => {
    if (Array.isArray(entry?.items)) return true;
    const text = String(entry?.text || '').trim();
    const path = String(entry?.path || '').trim();
    return text.endsWith('/') || path.endsWith('/');
};

const getDocumentKey = (name = '', path = '') => `${String(name || '').trim().toLowerCase()}|${String(path || '').trim().toLowerCase()}`;

const ContactsDocuments2 = ({
    idToken,
    contactGUID,
    selectedContactHouseholds = [],
    householdRows = [],
    householdsLoading = false,
    onRefreshHouseholds,
    onDocumentsCountChange,
    companySettings
}) => {
    const companyCtx = useContext(CompanyContext);
    const hasCompanyPortalDatabase = String(companySettings?.CompanyPortalDatabase || '').trim() !== '';
    const mountedRef = useRef(true);
    const lastRequestKeyRef = useRef('');

    const [loading, setLoading] = useState(false);
    const [folderEntries, setFolderEntries] = useState([]);
    const [viewMode, setViewMode] = useState('grid');
    const [pendingDocumentKeys, setPendingDocumentKeys] = useState([]);
    const [treeExpansionState, setTreeExpansionState] = useState({
        hasExpandable: false,
        allExpanded: true
    });
    const [treeExpandCommand, setTreeExpandCommand] = useState({
        mode: 'expand',
        nonce: 0
    });

    const sourceAccounts = useMemo(() => selectedContactHouseholds || [], [selectedContactHouseholds]);

    const allowedHouseholds = useMemo(() => {
        const rawHouseholds = [...new Set((sourceAccounts || []).map(item => normalizeWhitespace(item)).filter(Boolean))];
        const validHouseholdSet = new Set((householdRows || []).map(row => normalizeValue(row?.household)));
        if (validHouseholdSet.size === 0) return rawHouseholds;
        return rawHouseholds.filter(household => validHouseholdSet.has(normalizeValue(household)));
    }, [sourceAccounts, householdRows]);

    const handleSubmit = useCallback((households = []) => {
        if (!hasCompanyPortalDatabase) {
            setFolderEntries([]);
            setLoading(false);
            return;
        }

        const normalizedAllowedMap = new Map();
        (households || []).forEach((item) => {
            const normalized = normalizeWhitespace(item);
            if (!normalized) return;
            const key = normalizeValue(normalized);
            if (!normalizedAllowedMap.has(key)) normalizedAllowedMap.set(key, normalized);
        });
        const normalizedAllowed = Array.from(normalizedAllowedMap.values());
        if (normalizedAllowed.length === 0) {
            setFolderEntries([]);
            return;
        }

        const householdFolders = normalizedAllowed
            .map(toHouseholdFolder)
            .filter(Boolean);

        setLoading(true);
        fetch(`${process.env.REACT_APP_PORTAL_API_URL}/api/crm/filetree_post`, {
            method: 'POST',
            body: JSON.stringify({
                folder: householdFolders
            }),
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + (idToken || ""),
                'X-Database': companyCtx.company
            }
        })
            .then(r => r.json())
            .then(results => {
                if (!mountedRef.current) return;

                if (results.debug_message != null) {
                    utilities.addToast('Unable to load documents.', 5, 'danger');
                    setFolderEntries([]);
                    return;
                }

                const allEntries = Array.isArray(results?.data)
                    ? results.data
                    : (Array.isArray(results) ? results : []);

                const allowedHouseholdSet = new Set(normalizedAllowed.map(normalizeValue));
                const filteredEntries = allEntries.filter((entry) => {
                    const householdKey = getHouseholdKeyFromPath(entry?.path);
                    return !!householdKey && allowedHouseholdSet.has(householdKey);
                });

                setFolderEntries(filteredEntries);
            })
            .catch(() => {
                utilities.addToast('An error has occurred.', 5, 'danger');
                if (!mountedRef.current) return;
                setFolderEntries([]);
            })
            .finally(() => {
                if (!mountedRef.current) return;
                setLoading(false);
            });
    }, [idToken, companyCtx.company, hasCompanyPortalDatabase]);

    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    useEffect(() => {
        if (householdsLoading) return;

        const requestKey = `${contactGUID || ''}|${allowedHouseholds.map(normalizeValue).sort().join('|')}`;
        if (requestKey === lastRequestKeyRef.current) return;
        lastRequestKeyRef.current = requestKey;

        handleSubmit(allowedHouseholds);
    }, [
        contactGUID,
        householdsLoading,
        allowedHouseholds,
        handleSubmit
    ]);

    const handleDocumentAction = useCallback(async ({ name = '', path = '', source = 'download' } = {}) => {
        if (!hasCompanyPortalDatabase) return;

        const safeName = String(name || '').trim();
        const safePath = String(path || '').trim();

        if (!safeName || !safePath) return;

        const docKey = getDocumentKey(safeName, safePath);
        let shouldContinue = false;
        setPendingDocumentKeys(prev => {
            if (prev.includes(docKey)) return prev;
            shouldContinue = true;
            return [...prev, docKey];
        });
        if (!shouldContinue) return;

        const extension = getFileExtension(safeName, safePath);
        const isPdf = extension === 'pdf';
        const actionType = (source === 'file' && isPdf) ? 'view' : 'download';

        try {
            const response = await fetch(`${process.env.REACT_APP_PORTAL_API_URL}/api/crm/download`, {
                method: 'POST',
                body: JSON.stringify({
                    name: safeName,
                    path: safePath,
                    type: actionType
                }),
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + (idToken || ""),
                    'X-Database': companyCtx.company
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const contentType = (response.headers.get('content-type') || '').toLowerCase();
            if (contentType.includes('application/json')) {
                const results = await response.json();
                if (results?.debug_message != null || results?.success === false) {
                    throw new Error(results?.debug_message || 'Unable to download document.');
                }

                const possibleUrl = results?.url || results?.data?.url || results?.data?.downloadUrl || results?.data;
                if (typeof possibleUrl === 'string' && /^https?:\/\//i.test(possibleUrl)) {
                    window.open(possibleUrl, '_blank', 'noopener,noreferrer');
                    return;
                }

                utilities.addToast('Document request completed.', 3, 'success');
                return;
            }

            const textCandidate = await response.clone().text();
            const possibleTextUrl = String(textCandidate || '').trim();
            if (/^https?:\/\//i.test(possibleTextUrl)) {
                window.open(possibleTextUrl, '_blank', 'noopener,noreferrer');
                return;
            }

            const blob = await response.blob();
            const objectUrl = window.URL.createObjectURL(blob);

            if (actionType === 'view') {
                window.open(objectUrl, '_blank', 'noopener,noreferrer');
                setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60 * 1000);
                return;
            }

            const link = document.createElement('a');
            link.href = objectUrl;
            link.download = safeName;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(objectUrl);
        } catch (error) {
            utilities.addToast('Unable to download document.', 5, 'danger');
        } finally {
            if (mountedRef.current) {
                setPendingDocumentKeys(prev => prev.filter(item => item !== docKey));
            }
        }
    }, [idToken, companyCtx.company, hasCompanyPortalDatabase]);

    const isDocumentPending = useCallback((name = '', path = '') => {
        const docKey = getDocumentKey(name, path);
        return pendingDocumentKeys.includes(docKey);
    }, [pendingDocumentKeys]);

    const onToggleTreeExpandAll = useCallback(() => {
        const nextMode = treeExpansionState.allExpanded ? 'collapse' : 'expand';
        setTreeExpandCommand(prev => ({
            mode: nextMode,
            nonce: prev.nonce + 1
        }));
    }, [treeExpansionState.allExpanded]);

    const documentsCount = useMemo(() => {
        return (folderEntries || []).reduce((total, entry) => {
            if (isFolderEntry(entry)) {
                const items = Array.isArray(entry?.items) ? entry.items : [];
                return total + items.length;
            }
            return total + 1;
        }, 0);
    }, [folderEntries]);

    useEffect(() => {
        if (typeof onDocumentsCountChange === 'function') {
            onDocumentsCountChange(documentsCount);
        }
    }, [documentsCount, onDocumentsCountChange]);

    const isDocumentsLoading = householdsLoading || loading;
    const hasNoDocuments = !isDocumentsLoading && documentsCount === 0;

    return (
        <Container fluid className='px-0'>
            <Row className='mb-3'>
                <Col className='d-flex align-items-center'>
                    <ButtonGroup>
                        <Button
                            outline={viewMode !== 'tree'}
                            onClick={() => setViewMode('tree')}
                            color="primary"
                        >
                            Folder Layout
                        </Button>
                        <Button
                            outline={viewMode !== 'grid'}
                            onClick={() => setViewMode('grid')}
                            color="primary"
                        >
                            Grid Layout
                        </Button>
                    </ButtonGroup>
                    {viewMode === 'tree' && treeExpansionState.hasExpandable && !hasNoDocuments && !isDocumentsLoading && (
                        <Button
                            color="secondary"
                            className="ml-3"
                            onClick={onToggleTreeExpandAll}
                        >
                            {treeExpansionState.allExpanded ? 'Collapse All' : 'Expand All'}
                        </Button>
                    )}
                </Col>
                <Col className='text-muted text-right'>
                </Col>
            </Row>

            {isDocumentsLoading ? (
                <>
                    <h4>Loading Documents...</h4>
                    <Loading />
                </>
            ) : hasNoDocuments ? (
                <h4>No Documents Available</h4>
            ) : viewMode === 'grid' ? (
                <ContactsTableView
                    folderEntries={folderEntries}
                    sourceAccounts={sourceAccounts}
                    onDocumentAction={handleDocumentAction}
                    isDocumentPending={isDocumentPending}
                />
            ) : (
                <ContactsTreeView
                    folderEntries={folderEntries}
                    sourceAccounts={sourceAccounts}
                    onDocumentAction={handleDocumentAction}
                    isDocumentPending={isDocumentPending}
                    expandCommand={treeExpandCommand}
                    onExpansionStateChange={setTreeExpansionState}
                />
            )}
        </Container>
    );
};

export default ContactsDocuments2;
